import { IonPage } from '@ionic/react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { useMyMonitoringGetSite, useMyMonitoringGetSiteList } from '@apps-common/apis/my-monitoring/site'
import { SiteSelect } from '@apps-common/apps/my-monitoring/pages/site/components/site-select'
import { SiteStatusMyMonitoring } from '@apps-common/apps/my-monitoring/pages/site/components/site-status'
import { ImageWithFallback } from '@apps-common/components/common'
import { MyMonitoringLogoColor } from '@apps-common/components/common/logos'
import { UserProfileButton } from '@apps-common/components/common/user'
import { ButtonRoundedSm } from '@apps-common/components/elements'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { NavbarMyMonitoring } from '@apps-common/components/navigation'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { imageUrlReplace } from '@apps-common/helpers/url-replace'
import { useGetDefaultSiteImage } from '@apps-common/hooks/useGetDefaultSiteImage'
import { useTheme } from '@apps-common/hooks/useTheme'
import { useMyMonitoringSiteContextStore, useMyMonitoringSiteViewStore } from '@apps-common/stores'
import { SitePageTransition } from './components'
import { SiteDetails } from './components/site-details'
import { SiteTriggerView, SiteUserView, SiteZoneView } from './components/views'
import { SiteHomeView } from './components/views/SiteHomeView'
import { SiteScheduleView } from './components/views/SiteScheduleView'

const siteViews = [
  {
    key: 'home',
    Component: SiteHomeView,
    isDefault: true
  },
  {
    key: 'users',
    Component: SiteUserView
  },
  {
    key: 'zones',
    Component: SiteZoneView
  },
  {
    key: 'schedules',
    Component: SiteScheduleView
  },
  {
    key: 'triggers',
    Component: SiteTriggerView
  }
]

const NoSitesAvailableCard = ({ loading, refetch }: { loading: boolean; refetch: () => void }) => (
  <div className='mx-auto flex min-h-[45dvh] w-full max-w-[500px] items-center justify-center'>
    <div className='w-full'>
      <div className='text-text text-[0.9rem] text-center mb-4 bg-surface-2 border border-border rounded-3xl p-6 flex flex-col items-center justify-center gap-4'>
        <commonIcons.warning className='text-warning text-[2.4rem]' />
        <span>No sites available.</span>
        <p className='text-muted jost-light'>This account is not associated with any sites.</p>
        <p className='text-muted jost-light'>Please ask a site administrator to add you, or contact your provider for assistance.</p>
        <ButtonRoundedSm
          type='button'
          size='sm'
          text='Reload sites'
          loading={loading}
          icon={<commonIcons.reload className='text-[1rem]' />}
          className='!grow-0 bg-mm-brand text-on-mm-brand hover:bg-mm-brand/90 mt-4'
          callback={() => refetch()}
        />
      </div>
    </div>
  </div>
)

export const SitePage = () => {
  const [siteId, setSiteId] = useState<string>()
  const history = useHistory()
  const location = useLocation()

  const { isLoading, isError, data, refetch } = useMyMonitoringGetSite({ config: { enabled: !!siteId }, unitId: siteId })
  const {
    isLoading: isLoadingSiteList,
    isFetching: isFetchingSiteList,
    isError: isErrorSiteList,
    data: siteList,
    refetch: refetchSiteList
  } = useMyMonitoringGetSiteList({})
  const { defaultImageUrl } = useGetDefaultSiteImage(data?.siteId)
  const { changeContextTheme, currentContextTheme } = useTheme()
  const { viewKey, setViewKey } = useMyMonitoringSiteViewStore()
  const { setIsAdmin } = useMyMonitoringSiteContextStore()

  const defaultViewKey = useMemo(() => siteViews.find((v) => v.isDefault)?.key || 'home', [])
  const previousViewKeyRef = useRef(defaultViewKey)
  const [navbarHeight, setNavbarHeight] = useState<number | undefined>()
  const activeView = useMemo(() => siteViews.find((v) => v.key === viewKey) ?? siteViews[0], [viewKey])
  const isHomeActive = viewKey === defaultViewKey
  const isBack = viewKey === defaultViewKey && previousViewKeyRef.current !== defaultViewKey
  const requestedSiteId = useMemo(() => new URLSearchParams(location.search).get('siteId')?.trim() || undefined, [location.search])
  const handleSiteChange = useCallback((id: string) => setSiteId(id), [])
  const clearRequestedSiteId = useCallback(() => {
    const params = new URLSearchParams(location.search)

    if (!params.has('siteId')) return

    params.delete('siteId')

    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
      hash: location.hash
    })
  }, [history, location.hash, location.pathname, location.search])

  const loading = isLoading || isError
  const noSites = !siteId && !!siteList && siteList.length === 0 && !isLoadingSiteList && !isErrorSiteList

  useEffect(() => {
    setViewKey(defaultViewKey)
  }, [defaultViewKey, setViewKey, siteId])

  useEffect(() => {
    if (isLoading || !data?.theme) {
      // changeContextTheme(null)
    } else {
      changeContextTheme(data?.theme)
    }

    // return () => changeContextTheme(null)
  }, [changeContextTheme, data?.theme, isLoading])

  useEffect(() => {
    setIsAdmin(data?.isAdmin ?? false)
  }, [data?.isAdmin, setIsAdmin])

  useEffect(() => {
    previousViewKeyRef.current = viewKey
  }, [viewKey])

  useEffect(() => {
    if (requestedSiteId) {
      refetchSiteList()
    }
  }, [refetchSiteList, requestedSiteId])

  const startComponents = (
    <div className='flex sm:hidden items-center justify-start gap-[10px] mt-[2px] ml-[42px]'>
      <SiteSelect
        site={data}
        selectedSiteId={siteId}
        callback={handleSiteChange}
        requestedSiteId={requestedSiteId}
        onRequestedSiteIdConsumed={clearRequestedSiteId}
      />
      {/* <ThemeSelector /> */}
    </div>
  )
  const icons: any = (
    <div className='flex items-center justify-end gap-2'>
      <div className='hidden sm:block'>
        <SiteSelect
          site={data}
          selectedSiteId={siteId}
          callback={handleSiteChange}
          requestedSiteId={requestedSiteId}
          onRequestedSiteIdConsumed={clearRequestedSiteId}
        />
      </div>
      <div className='sm:mb-1 border border-text/50 rounded-full overflow-hidden shadow-[0_18px_45px_rgba(15,23,42,0.10),0_4px_12px_rgba(15,23,42,0.06)]'>
        <UserProfileButton />
      </div>
    </div>
  )

  const logoImgClasses = useMemo(
    () =>
      !!currentContextTheme?.myMonitoringLogoBg
        ? 'max-w-[210px] max-h-[40px] sm:max-h-[56px]'
        : 'max-w-[210px] max-h-[53px] mb-[0px] sm:max-h-[68px]',
    [currentContextTheme]
  )

  const logoPaddingClasses = useMemo(() => (!!currentContextTheme?.myMonitoringLogoBg ? 'px-3 py-2' : null), [currentContextTheme])

  return (
    <IonPage className='!flex flex-col'>
      <NavbarMyMonitoring transparent startComponents={startComponents} icons={icons} onHeightChange={setNavbarHeight}>
        <motion.div {...motionFadeIn(0.35)} className='mx-auto grow flex relative'>
          <div className='flex items-center justify-center mx-auto h-[56px] sm:h-[72px]'>
            <ImageWithFallback
              keyPrefix='site-branding-logo'
              src={currentContextTheme?.logoPrimary || ''}
              alt='Company Logo'
              imageClasses={logoImgClasses}
              containerClasses={classNames('bg-ctx-mm-logo-bg rounded-xl mx-auto', logoPaddingClasses)}
              Fallback={
                <div className='h-[56px] px-3 py-2'>
                  <MyMonitoringLogoColor classes='max-h-[40px] sm:max-h-[56px] mx-auto' />
                </div>
              }
              Loader={<div className='animate-pulse bg-skeleton-hi h-[56px] sm:h-[64px] rounded-xl min-w-[160px]' />}
              isLoadingOverride={(!data && !noSites) || isLoading}
            />
          </div>
        </motion.div>
        <div className='flex items-center justify-center'>
          <div className='sm:bg-ctx-bg/30 sm:backdrop-blur-lg sm:p-2 rounded-[35px] flex flex-col items-center justify-center sm:mt-[3dvh]'>
            {!noSites && (
              <>
                <SiteDetails siteLoading={loading} site={data} onSiteChange={(id) => setSiteId(id)} />
                {/* <div className='mb-[8px] border-b border-ctx-mm-brand/20 w-[70%]' /> */}
                <SiteStatusMyMonitoring siteLoading={loading} site={data} itemClasses='text-ctx-text' />
              </>
            )}
          </div>
        </div>
      </NavbarMyMonitoring>
      <div className='z-[-1] fixed top-0 left-0 w-full min-h-[50vh] overflow-hidden bg-transparent'>
        <div className='z-[99] absolute top-0 left-0 w-full h-[101%] bg-gradient-to-t from-ctx-bg from-0% to-100% to-ctx-bg/20 sm:to-ctx-bg/[0.35] backdrop-blur-lg' />
        <AnimatePresence>
          {data && (
            <motion.div
              key={`site-banner-image-${data?.siteId}`}
              {...motionFadeIn(0.3, 0.3)}
              style={{
                backgroundImage: `url("${imageUrlReplace(data?.siteImageUrl) || defaultImageUrl}")`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover'
              }}
              className='z-[2] absolute top-0 left-0 w-full h-[50vh]'
            />
          )}
        </AnimatePresence>
        <div className='z-[1] absolute top-0 left-0 w-full h-full bg-text/40' />
      </div>
      <LayoutContainer
        noSidebar
        contentScrollable={!isHomeActive}
        contentOverflowVisible={isHomeActive}
        forceOverscroll={isHomeActive}
        bgClasses='!relative !bg-transparent z-[10]'
        ionStyle={{
          '--background': 'transparent'
        }}
      >
        <LayoutPadding
          containerTopPadding={classNames('!pt-[0px] sm:!pt-0', isHomeActive && '!pb-0 md:!pb-0')}
          topPadding=''
          classes={classNames('max-w-full', isHomeActive && '!pb-0 md:!pb-0')}
        >
          {!!navbarHeight && (
            <motion.div
              {...motionFadeIn(0.2)}
              className={classNames(
                'px-[0.5rem] sm:px-[1rem] max-w-[min(450px,100%)] md:max-w-[min(900px,100%)] mx-auto scrollbar-none pb-[10px] sm:pb-0',
                isHomeActive ? 'h-[100dvh] min-h-0 overflow-hidden' : 'min-h-[100dvh]'
              )}
              style={{ paddingTop: navbarHeight + 20 }}
            >
              <div className={classNames('w-full flex flex-col items-center justify-start', isHomeActive && 'h-full min-h-0')}>
                <div className={classNames('w-full', isHomeActive && 'h-full min-h-0')}>
                  <div className={classNames('relative w-full', isHomeActive && 'h-full min-h-0')}>
                    {noSites ? (
                      <NoSitesAvailableCard loading={isFetchingSiteList} refetch={() => refetchSiteList()} />
                    ) : (
                      <motion.div
                        initial={false}
                        animate={{
                          opacity: isHomeActive ? 1 : 0,
                          x: isHomeActive ? 0 : -24,
                          transition: { duration: 0.22, ease: 'easeOut' }
                        }}
                        className={classNames(
                          'w-full',
                          isHomeActive && 'h-full min-h-0',
                          !isHomeActive && 'absolute inset-0 pointer-events-none'
                        )}
                      >
                        <SiteHomeView loading={loading} refetch={refetch} site={data} setSiteId={setSiteId} setView={setViewKey} />
                      </motion.div>
                    )}

                    {!noSites && !isHomeActive && (
                      <SitePageTransition viewKey={viewKey} direction={isBack ? 'forward' : 'back'}>
                        <activeView.Component
                          loading={loading}
                          refetch={refetch}
                          site={data}
                          setSiteId={setSiteId}
                          back={() => setViewKey(defaultViewKey)}
                          setView={setViewKey}
                        />
                      </SitePageTransition>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </LayoutPadding>
      </LayoutContainer>
    </IonPage>
  )
}
